import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Delete,
  Search,
  Add,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setNodes,
  setSelectedNode,
  setPage,
  setPageSize,
  setSearch,
  clearSelectedNode,
  addNode,
  updateNode as updateNodeAction,
  deleteNode as deleteNodeAction,
} from '../../store/slices/nodesSlice';
import { GET_NODES_LIST, GET_NODE } from '../../graphql/queries/nodes.queries';
import { CREATE_NODE, UPDATE_NODE, DELETE_NODE } from '../../graphql/mutations/nodes.mutations';
import {
  FindNodeListInput,
  FindNodeInput,
  CreateNodeInput,
  UpdateNodeInput,
  DeleteNodeInput,
  CreateNodeResponse,
  UpdateNodeResponse,
  DeleteNodeResponse,
  NodesListResponse,
  NodeResponse,
  Node,
} from '../../types/nodes.types';
import ViewNodeModal from '../../components/Nodes/ViewNodeModal';
import CreateEditNodeModal, { NodeFormData } from '../../components/Nodes/CreateEditNodeModal';
import ConfirmDeleteNodeDialog from '../../components/Nodes/ConfirmDeleteNodeDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './Nodes.css';

const Nodes: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nodes, loading, error, pagination, search } = useAppSelector(
    (state) => state.nodes
  );

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Delete state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<Node | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [fetchNodes, { loading: fetchingList }] = useLazyQuery<
    { nodes: NodesListResponse },
    { params: FindNodeListInput }
  >(GET_NODES_LIST, { fetchPolicy: 'network-only' });

  const [fetchNode] = useLazyQuery<
    { node: NodeResponse },
    { params: FindNodeInput }
  >(GET_NODE, { fetchPolicy: 'network-only' });

  const [createNodeMutation] = useMutation<
    { createNode: CreateNodeResponse },
    { params: CreateNodeInput }
  >(CREATE_NODE);

  const [updateNodeMutation] = useMutation<
    { updateNode: UpdateNodeResponse },
    { params: UpdateNodeInput }
  >(UPDATE_NODE);

  const [deleteNodeMutation] = useMutation<
    { deleteNode: DeleteNodeResponse },
    { params: DeleteNodeInput }
  >(DELETE_NODE);

  useEffect(() => {
    loadNodes();
  }, [pagination.page, pagination.pageSize, search]);

  const loadNodes = async () => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchNodes({
        variables: {
          params: {
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.nodes?.success && data.nodes.data) {
        dispatch(setNodes(data.nodes.data));
      } else {
        const errorMessage = data?.nodes?.message || 'Error al cargar nodos';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchNode({
        variables: { params: { ids: [id] } },
      });

      if (data?.node?.success && data.node.data) {
        const node = data.node.data[0];
        dispatch(setSelectedNode(node));
        setViewModalOpen(true);
      } else {
        dispatch(setError(data?.node?.message || 'Error al cargar nodo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchNode({
        variables: { params: { ids: [id] } },
      });

      if (data?.node?.success && data.node.data) {
        const node = data.node.data[0];
        dispatch(setSelectedNode(node));
        setModalMode('edit');
        setSaveError(null);
        setCreateEditModalOpen(true);
      } else {
        dispatch(setError(data?.node?.message || 'Error al cargar nodo'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCreate = () => {
    dispatch(clearSelectedNode());
    setModalMode('create');
    setSaveError(null);
    setCreateEditModalOpen(true);
  };

  const handleSave = async (formData: NodeFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      if (modalMode === 'create') {
        const { data: responseData } = await createNodeMutation({
          variables: {
            params: {
              code: formData.code,
              name: formData.name,
              keywords: formData.keywords,
              semanticWeight: formData.semanticWeight,
              order: formData.order,
            },
          },
        });

        if (responseData?.createNode?.success) {
          if (responseData.createNode.data) {
            dispatch(addNode(responseData.createNode.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedNode());
          setSaveError(null);
          setSuccessMessage('Nodo creado exitosamente');
        } else {
          const msg = responseData?.createNode?.message || 'Error al crear nodo';
          const errs = responseData?.createNode?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        if (!formData.id) {
          setSaveError('ID de nodo no disponible');
          return;
        }

        const { data: responseData } = await updateNodeMutation({
          variables: {
            params: {
              id: formData.id,
              code: formData.code,
              name: formData.name,
              keywords: formData.keywords,
              semanticWeight: formData.semanticWeight,
              order: formData.order,
            },
          },
        });

        if (responseData?.updateNode?.success) {
          if (responseData.updateNode.data) {
            dispatch(updateNodeAction(responseData.updateNode.data));
          }
          setCreateEditModalOpen(false);
          dispatch(clearSelectedNode());
          setSaveError(null);
          setSuccessMessage('Nodo actualizado exitosamente');
        } else {
          const msg = responseData?.updateNode?.message || 'Error al actualizar nodo';
          const errs = responseData?.updateNode?.errors || [];
          setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (node: Node) => {
    setNodeToDelete(node);
    setDeleteError(null);
    setConfirmDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setNodeToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!nodeToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteNodeMutation({
        variables: { params: { ids: [nodeToDelete.id] } },
      });

      if (responseData?.deleteNode?.success) {
        dispatch(deleteNodeAction(nodeToDelete.id));
        setConfirmDeleteOpen(false);
        setNodeToDelete(null);
        setSuccessMessage('Nodo eliminado exitosamente');
      } else {
        const msg = responseData?.deleteNode?.message || 'Error al eliminar nodo';
        const errs = responseData?.deleteNode?.errors || [];
        setDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedNode());
  };

  const handleCloseCreateEditModal = () => {
    setCreateEditModalOpen(false);
    dispatch(clearSelectedNode());
    setSaveError(null);
    setSaving(false);
  };

  const columns: GridColDef<Node>[] = [
    {
      field: 'code',
      headerName: 'Código',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'keywords',
      headerName: 'Palabras Clave',
      flex: 1,
      minWidth: 200,
      valueGetter: (value: string[]) => value?.join(', ') || '',
    },
    {
      field: 'semanticWeight',
      headerName: 'Peso Semántico',
      width: 140,
    },
    {
      field: 'order',
      headerName: 'Orden',
      width: 100,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 140,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="nodes-container">
        <Paper elevation={2} className="nodes-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Nodos</Typography>
          </Breadcrumbs>

          <Box className="nodes-header">
            <Typography variant="h4" component="h1">
              Nodos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administración de nodos del sistema
            </Typography>
          </Box>

          <Box className="nodes-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar nodo..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{ ml: 1 }}
              >
                Buscar
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
            >
              Nuevo Nodo
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={nodes}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>

        <ViewNodeModal open={viewModalOpen} onClose={handleCloseViewModal} />
        <CreateEditNodeModal
          open={createEditModalOpen}
          onClose={handleCloseCreateEditModal}
          onSave={handleSave}
          mode={modalMode}
          saving={saving}
          saveError={saveError}
        />
        <ConfirmDeleteNodeDialog
          open={confirmDeleteOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          node={nodeToDelete}
          deleting={deleting}
          deleteError={deleteError}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </MainLayout>
  );
};

export default Nodes;
